import type { UxCondition } from "./model"
import { message } from "./messages"

/** The outcome of a WidgetIR condition. `unavailable` is a state of its own, never a default. */
export type ConditionOutcome = { readonly kind: "value"; readonly value: boolean } | { readonly kind: "unavailable"; readonly reason: string; readonly issue?: number }

/**
 * Where a condition's declared inputs come from, looked up by the exact ids it declares: a record by
 * its data binding, a draft by its form, context by its page. A source that is not here is absent.
 */
export interface ConditionSources {
  /** A binding's record here: its business values and, separately, its runtime key. */
  readonly record: (bindingId: string) => { readonly values: Readonly<Record<string, unknown>>; readonly key: string } | undefined
  /** Current selected values from the exact list binding; undefined means unavailable. */
  readonly selection?: (bindingId: string) => readonly Readonly<Record<string, unknown>>[] | undefined
  /** The current row of the exact child grid named by a field condition. */
  readonly row?: { readonly gridId: string; readonly rowId: string; readonly values: Readonly<Record<string, unknown>> }
  readonly draft?: { readonly formId: string; readonly values: Readonly<Record<string, unknown>> }
  readonly page?: { readonly pageId: string; readonly context: Readonly<Record<string, unknown>> }
}

/** Norn's ZEN evaluator (`@norn/checker-wasm` `evaluateExpression`): a value, or a typed error. */
export type NornEvaluate = (expression: string, inputs: Readonly<Record<string, unknown>>, locale?: string) => { readonly ok: true; readonly value: unknown } | { readonly ok: false; readonly code: string; readonly message: string }

/** Where the evaluator is: still loading, failed to load, or ready. */
export type EvaluatorState = { readonly status: "loading" } | { readonly status: "failed"; readonly reason: string } | { readonly status: "ready"; readonly evaluate: NornEvaluate }

/**
 * Evaluate a declared condition with Norn's evaluator over exactly the inputs it declares. A missing
 * declared input, an evaluation error or an evaluator that is not ready all
 * report `unavailable` with the reason: the UI never treats them as true or false.
 */
export function evaluateDisplayValue(condition: UxCondition | undefined | null, sources: ConditionSources, evaluator: EvaluatorState, locale = "en"): DisplayOutcome | null {
  if (!condition) return null
  if (evaluator.status === "loading") return { kind: "unavailable", reason: message(locale, "condition.evaluatingTitle") }
  if (evaluator.status === "failed") return { kind: "unavailable", reason: message(locale, "condition.loadFailed", { error: evaluator.reason }) }
  const declared = condition.inputs
  const input: Record<string, unknown> = {}
  if (declared.recordBindingId) {
    const record = sources.record(declared.recordBindingId)
    if (!record) return { kind: "unavailable", reason: message(locale, "condition.recordUnavailable", { bindingId: declared.recordBindingId }) }
    input.record = record.values
    input.recordKey = record.key
  }
  if (declared.rowGridId) {
    if (sources.row?.gridId !== declared.rowGridId) return { kind: "unavailable", reason: message(locale, "condition.rowUnavailable", { gridId: declared.rowGridId }) }
    input.row = sources.row.values
  }
  if (declared.draftFormId) {
    if (sources.draft?.formId !== declared.draftFormId) return { kind: "unavailable", reason: message(locale, "condition.draftUnavailable", { formId: declared.draftFormId }) }
    input.draft = sources.draft.values
  }
  if (declared.selectionBindingId) {
    const selection = sources.selection?.(declared.selectionBindingId)
    if (!selection) return { kind: "unavailable", reason: message(locale, "condition.selectionUnavailable") }
    input.selection = selection
  }
  if (declared.contextPageId) {
    if (sources.page?.pageId !== declared.contextPageId) return { kind: "unavailable", reason: message(locale, "condition.contextUnavailable", { pageId: declared.contextPageId }) }
    input.context = sources.page.context
  }
  const result = evaluator.evaluate(condition.expression, input, locale)
  if (!result.ok) return { kind: "unavailable", reason: message(locale, "condition.failed", { code: result.code, error: result.message }) }
  return { kind: "value", value: result.value }
}

/** Derived display values retain the evaluator's exact result, including decimal tokens. */
export type DisplayOutcome = { readonly kind: "value"; readonly value: unknown } | Extract<ConditionOutcome, { kind: "unavailable" }>

/** Conditions, visibility and display computations share the same evaluator and input construction. */
export function evaluateCondition(condition: UxCondition | undefined | null, sources: ConditionSources, evaluator: EvaluatorState, locale = "en"): ConditionOutcome | null {
  const result = evaluateDisplayValue(condition, sources, evaluator, locale)
  if (result === null || result.kind === "unavailable") return result
  return typeof result.value === "boolean" ? { kind: "value", value: result.value } : { kind: "unavailable", reason: message(locale, "condition.notBoolean") }
}

/** Shown or enabled only on a known `true`; anything else withholds the control. */
export function allows(outcome: ConditionOutcome | null): boolean {
  return outcome === null || (outcome.kind === "value" && outcome.value)
}
